import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Divider,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Button,
  Dialog,
  DialogContent,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Trans, useTranslation } from "react-i18next";
import PersonIcon from "@mui/icons-material/Person";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PlaceIcon from "@mui/icons-material/Place";
import MarkAsUnreadIcon from "@mui/icons-material/MarkAsUnread";
import { useTheme } from "@emotion/react";
import {
  app_title,
  endpoint,
  s3StaticImagePath,
  uanRegex,
} from "../config";
import axios from "axios";
import { customToast } from "../lib/toastLib";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import getMuthootFincorp from "../hooks/GET/getMuthootFincorp";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";

function MuthootFincorp() {
  const { t } = useTranslation();
  const theme = useTheme();
  const params = useParams();
  const authToken = useSelector((state) => state.token.token);

  const navigate = useNavigate();
  const [isPartnerNameVerified, setIsPartnerNameVerified] = useState(false)
  const [genderList, setGenderList] = useState([])
  const [maritalStatusList, setMaritalStatusList] = useState([])
  const [purposeOfLoanList, setPurposeOfLoanList] = useState([])
  const [categoryList, setCategoryList] = useState([])
  const [subCategoryList, setSubCategoryList] = useState([])
  const [natureList, setNatureList] = useState([])
  const [checkMFCConsent, setCheckMFCConsent] = useState(false)
  const [errors, setErrors] = useState({})
  const [isOpenRejectionModal, setIsOpenRejectionModal] = useState(false)

  useEffect(() => {
    if (params.creditapp_uuid) {
      setIsPartnerNameVerified(false);
      getMuthootFincorp(params.creditapp_uuid)
        .then((response) => {
          setGenderList(response?.data.gender);
          setMaritalStatusList(response?.data.maritalStatus);
          setPurposeOfLoanList(response?.data.purposeOfLoan);
          setNatureList(response?.data.nature);
          setCategoryList(response?.data.category || []);
          setIsPartnerNameVerified(true);
        })
        .catch((err) => {
          if (err.response && err.response?.data?.success === "false") {
            if (err.response?.data?.redirectionUrl) {
              window.open(err.response?.data?.redirectionUrl, "_self");
            } else if (err.response?.data?.message === "Rejected") {
              setIsPartnerNameVerified(false);
              setIsOpenRejectionModal(true);
            }
            else {
              setIsPartnerNameVerified(true);
              navigate('/404');
            }
          }
        });
    } else {
      setIsPartnerNameVerified(true);
    }
  }, [params]);

  const handleRejection = async () => {
    setIsOpenRejectionModal(false);
    try {
      const { data } = await axios.post(`${endpoint}/api/gold-loans/${params.creditapp_uuid}`);
      navigate(`/${data.redirect}`, { replace: true });
    } catch (error) {
      if (error.response) {
        customToast.error(error.response.data?.message);
      } else {
        customToast.error(error.message);
      }
    }
  }

  // state logic start
  const initialState = {
    purposeOfLoan: "",
    gender: "",
    maritalStatus: "",
    fathersName: "",
    mothersName: "",
    annualSalesTurnOver: "",
    uan: "",
    name: "",
    dateOfIncorporation: "",
    category: "",
    subCategory: "",
    nature: "",
    businessAddressLine1: "",
    businessAddressLine2: "",
    businessAddressPincode: "",
  };

  const [state, setState] = useState({ ...initialState });
  const [isSubmitBtnDisbaled, setIsSubmitBtnDisbaled] = useState(true);
  const logoURL = useSelector((state) => state.appInfo.logo);

  useEffect(() => {
    setIsSubmitBtnDisbaled(
      !state.purposeOfLoan ||
      !state.gender ||
      !state.maritalStatus ||
      !state.fathersName ||
      !state.dateOfIncorporation ||
      !state.mothersName ||
      !state.annualSalesTurnOver ||
      !state.name ||
      !state.category ||
      errors.uan ||
      (subCategoryList.length > 0 && !state.subCategory) || // Validate subcategory if subcategories exist
      !state.nature ||
      !state.businessAddressLine1 ||
      !state.businessAddressLine2 ||
      !state.businessAddressPincode ||
      !checkMFCConsent
    );
  }, [state, checkMFCConsent, subCategoryList]);

  const handleState = (e) => {
    const { name, value } = e.target;

    setErrors((prev) => {
      return {
        ...prev,
        [name]: "",
      };
    });

    if (name === "businessAddressPincode") {
      if (isNaN(Number(value)) || value.length > 6) {
        return;
      }
    }

    if (name === "uan" && value && !uanRegex.test(value)) {
      setErrors((prev) => {
        return {
          ...prev,
          uan: "Please enter a valid UAN",
        };
      });
    }

    setState((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleSelectState = (name, item) => {
    setErrors((prev) => {
      return {
        ...prev,
        [name]: "",
      };
    });
    setState((prev) => {
      return {
        ...prev,
        [name]: item,
      };
    });
  };

  const [submitLoading, setSubmitLoading] = useState(false);
  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors([])
    const formData = new FormData();

    formData.append("purposeOfLoan", state.purposeOfLoan);
    formData.append("gender", state.gender);
    formData.append("maritalStatus", state.maritalStatus);
    formData.append("fathersName", state.fathersName);
    formData.append("mothersName", state.mothersName);
    formData.append("annualSalesTurnOver", state.annualSalesTurnOver);
    if(state.uan) formData.append("uan", state.uan);
    formData.append("name", state.name);
    formData.append("dateOfIncorporation", state.dateOfIncorporation);
    formData.append("category", state.category);
    formData.append("subCategory", state.subCategory || "");
    formData.append("nature", state.nature);
    formData.append("businessAddressLine1", state.businessAddressLine1);
    formData.append("businessAddressLine2", state.businessAddressLine2);
    formData.append("businessAddressPincode", state.businessAddressPincode);

    setSubmitLoading(true);
    axios
      .post(`${endpoint}/api/muthootfincorp-intake/${params.creditapp_uuid}`, formData)
      .then((res) => {
        if (res.data.success === "false") {
          customToast.error("Your application has been rejected");
        } else {
          window.open(res.data.redirectionUrl, "_self");
        }
        setSubmitLoading(false);
      })
      .catch((error) => {
        setSubmitLoading(false);
        if (error.response.status === 422) {
          const errors = error.response.data.errors;
          setErrors(errors)
        }
        if (error.response) {
          customToast.error(error.response.data?.message);
        } else {
          customToast.error(error.message);
        }
      });
  };

  return (
    <>
    <Box sx={{ overflow: "auto", paddingX: 3, paddingY: 5 }}>
      <Helmet>
        <title>Merchant Form {app_title && `| ${app_title}`} </title>
      </Helmet>
      {!isPartnerNameVerified && <Box sx={{ width: '100%', height: '100vh', postion: 'fixed', top: 0, left: 0, zIndex: 99, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress color="inherit" />
      </Box>}
      <Grid
        container
        alignItems="center"
        sx={{
          overflow: "auto",
          justifyContent: { xs: "initial", sm: "center" },
        }}
      >
        <Grid
          item
          xs={12}
          sm={8}
          md={6}
        >
          {authToken &&
            <Grid item
              alignItems="center"
              xs={12}
              sx={{ minWidth: 350, margin: '0 auto' }}>
              <Button
                size="small"
                color="primary"
                startIcon={<ArrowBackIosIcon />}
                sx={{ marginBottom: 3 }}
                onClick={() => navigate(`/user/offers`)}
              >
                {t("Return to Offers")}
              </Button>
            </Grid>
          }
          {logoURL &&
            <Grid
              container
              sx={{ flexDirection: "column", alignItems: "center", mt: 4, mb: 5 }}
            >
              <Grid item>
                <div style={{ maxWidth: "240px", margin: "0 auto", marginBottom: "20px" }}>
                  <img
                    src={`${s3StaticImagePath}/1756979958-kqlxrxH0E9.webp`}
                    alt="lender_logo"
                    style={{ width: '100%' }}
                  />
                </div>
                <Typography variant="h6" color="secondary" fontWeight="bold" align="center">
                  {t("Thank you for your interest with Muthoot EDI loans, we need a few more information to approve your case.")}
                </Typography>
              </Grid>
              <Divider flexItem variant="middle" sx={{ marginTop: 2 }} />
            </Grid>}
          <Grid component="form" onSubmit={handleSubmit} autoComplete="off">
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ marginTop: 2, marginBottom: 1 }}
            >
              {t("Purpose of Loan")}
              <span style={{ color: "red", marginLeft: "6px" }}>*</span>
            </Typography>
            <Autocomplete
              name="purposeOfLoan"
              disablePortal
              id="combo-box-demo"
              onChange={(_, item) => handleSelectState("purposeOfLoan", item)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
              value={state.purposeOfLoan}
              options={purposeOfLoanList}
              renderInput={(params) => <TextField {...params} error={errors.purposeOfLoan} helperText={errors.purposeOfLoan} />}
            />

            <Divider sx={{ marginTop: 2 }} />

            <Box paddingY={2}>
              <Typography variant="h6" paddingBottom={2}>
                Personal Details
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginTop: 2, marginBottom: 1 }}
              >
                {t("Gender")}
                <span style={{ color: "red", marginLeft: "6px" }}>*</span>
              </Typography>
              <Autocomplete
                name="gender"
                disablePortal
                id="combo-box-demo"
                onChange={(_, item) => handleSelectState("gender", item)}
                value={state.gender}
                options={genderList}
                renderInput={(params) => <TextField {...params} error={errors.gender} helperText={errors.gender} />}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginTop: 2, marginBottom: 1 }}
              >
                {t("Marital Status")}
                <span style={{ color: "red", marginLeft: "6px" }}>*</span>
              </Typography>
              <Autocomplete
                name="maritalStatus"
                disablePortal
                id="combo-box-demo"
                onChange={(_, item) => handleSelectState("maritalStatus", item)}
                value={state.maritalStatus}
                options={maritalStatusList}
                renderInput={(params) => <TextField {...params} error={errors.maritalStatus} helperText={errors.maritalStatus} />}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginTop: 2 }}
              >
                {t("Father's Name")}
                <span style={{ color: "red", marginLeft: "6px" }}>*</span>
              </Typography>
              <TextField
                type="text"
                margin="dense"
                fullWidth
                name="fathersName"
                value={state.fathersName}
                onChange={handleState}
                error={errors.fathersName}
                helperText={errors.fathersName}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginTop: 2 }}
              >
                {t("Mother's Name")}
                <span style={{ color: "red", marginLeft: "6px" }}>*</span>
              </Typography>
              <TextField
                type="text"
                margin="dense"
                fullWidth
                name="mothersName"
                value={state.mothersName}
                onChange={handleState}
                error={errors.mothersName}
                helperText={errors.mothersName}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Divider sx={{ marginTop: 2 }} />

            <Box paddingY={2}>
              <Typography variant="h6" paddingBottom={2}>
                Business Details
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginTop: 2 }}
              >
                {t("Annual Sales TurnOver")}
                <span style={{ color: "red", marginLeft: "6px" }}>*</span>
              </Typography>
              <TextField
                type="number"
                margin="dense"
                fullWidth
                name="annualSalesTurnOver"
                value={state.annualSalesTurnOver}
                onChange={handleState}
                error={errors.annualSalesTurnOver}
                helperText={errors.annualSalesTurnOver}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CurrencyRupeeIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginTop: 2 }}
              >
                {t("UAN")}
              </Typography>
              <TextField
                type="text"
                margin="dense"
                fullWidth
                name="uan"
                value={state.uan}
                onChange={handleState}
                error={errors.uan}
                helperText={errors.uan}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <span style={{ color: "gray", marginLeft: "6px", fontSize: "12px" }}>
                {t("Example: UDYAM-MH-12-1234567")}
              </span>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginTop: 2 }}
              >
                {t("Business Name")}
                <span style={{ color: "red", marginLeft: "6px" }}>*</span>
              </Typography>
              <TextField
                type="text"
                margin="dense"
                fullWidth
                name="name"
                value={state.name}
                onChange={handleState}
                error={errors.name}
                helperText={errors.name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginTop: 2 }}
              >
                {t("Date Of Incorporation")}
                <span style={{ color: "red", marginLeft: "6px" }}>*</span>
              </Typography>
              <TextField
                type="date"
                margin="dense"
                fullWidth
                name="dateOfIncorporation"
                value={state.dateOfIncorporation}
                onChange={handleState}
                error={errors.dateOfIncorporation}
                helperText={errors.dateOfIncorporation}
                inputProps={{
                  max: new Date().toISOString().split('T')[0]
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DateRangeIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginTop: 2, marginBottom: 1 }}
              >
                {t("Category")}
                <span style={{ color: "red", marginLeft: "6px" }}>*</span>
              </Typography>
              <Autocomplete
                name="category"
                disablePortal
                id="combo-box-demo"
                onChange={(e, item) => {
                  setErrors((prev) => {
                    return {
                      ...prev,
                      category: "",
                    };
                  });
                  setState((prev) => {
                    return {
                      ...prev,
                      category: item,
                      subCategory: "",
                    };
                  });
                  if (item && typeof categoryList === 'object') {
                    setSubCategoryList(categoryList[item] || []);
                  } else {
                    setSubCategoryList([]);
                  }
                }}
                value={state.category}
                options={Object.keys(categoryList)}
                renderInput={(params) => <TextField {...params} error={errors.category} helperText={errors.category} />}
              />
              {state.category && subCategoryList.length > 0 && (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ marginTop: 2, marginBottom: 1 }}
                  >
                    {t("Sub Category")}
                    <span style={{ color: "red", marginLeft: "6px" }}>*</span>
                  </Typography>
                  <Autocomplete
                    name="subCategory"
                    disablePortal
                    id="subcategory-combo-box"
                    onChange={(_, item) => handleSelectState("subCategory", item)}
                    value={state.subCategory}
                    options={subCategoryList}
                    renderInput={(params) => <TextField {...params} error={errors.subCategory} helperText={errors.subCategory} />}
                  />
                </>
              )}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginTop: 2, marginBottom: 1 }}
              >
                {t("Nature")}
                <span style={{ color: "red", marginLeft: "6px" }}>*</span>
              </Typography>
              <Autocomplete
                name="nature"
                disablePortal
                id="nature-combo-box"
                onChange={(_, item) => handleSelectState("nature", item)}
                value={state.nature}
                options={natureList}
                renderInput={(params) => <TextField {...params} error={errors.nature} helperText={errors.nature} />}
              />
            </Box>

            <Divider sx={{ marginTop: 2 }} />

            <Box paddingY={2}>
              <Typography variant="h6" paddingBottom={2}>
                Business Address
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginTop: 2 }}
              >
                {t("Address Line 1")}
                <span style={{ color: "red", marginLeft: "6px" }}>*</span>
              </Typography>
              <TextField
                type="text"
                margin="dense"
                fullWidth
                name="businessAddressLine1"
                value={state.businessAddressLine1}
                onChange={handleState}
                error={errors.businessAddressLine1}
                helperText={errors.businessAddressLine1}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PlaceIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginTop: 2 }}
              >
                {t("Address Line 2")}
                <span style={{ color: "red", marginLeft: "6px" }}>*</span>
              </Typography>
              <TextField
                type="text"
                margin="dense"
                fullWidth
                name="businessAddressLine2"
                value={state.businessAddressLine2}
                onChange={handleState}
                error={errors.businessAddressLine2}
                helperText={errors.businessAddressLine2}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PlaceIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginTop: 2 }}
              >
                {t("Postal code")}
                <span style={{ color: "red", marginLeft: "6px" }}>*</span>
              </Typography>
              <TextField
                type="text"
                margin="dense"
                fullWidth
                name="businessAddressPincode"
                value={state.businessAddressPincode}
                onChange={handleState}
                error={errors.businessAddressPincode}
                helperText={errors.businessAddressPincode}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MarkAsUnreadIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  name="agreeMFCConsentCheck"
                  sx={{ alignSelf: "flex-start" }}
                  checked={checkMFCConsent}
                  onChange={(e) => setCheckMFCConsent(e.target.checked)}
                />
              }
              sx={{ textAlign: "justify", width: "100%" }}
              label={
                <Trans
                  i18nKey="MF Consents"
                  t={t}
                  components={[
                    <a href="https://finbox-cdn.s3.ap-south-1.amazonaws.com/assets/MFL-T%26C-Edited.pdf" style={{ textDecoration: "underline" }} target="_blank" />,
                    <a href="https://muthootfincorp.com/privacy-policy" style={{ textDecoration: "underline" }} target="_blank" />,
                  ]}
                />
              }
            />

            <LoadingButton
              color="primary"
              variant="contained"
              size="large"
              fullWidth
              type="submit"
              disabled={isSubmitBtnDisbaled}
              loading={submitLoading}
              sx={{ marginTop: 3 }}
            >
              {t("Submit")}
            </LoadingButton>
          </Grid>
        </Grid>
      </Grid>
    </Box>
    {isOpenRejectionModal && <Dialog
        open={!!isOpenRejectionModal}
        scroll="paper"
        fullWidth
        autoComplete="off"
      >
        <DialogContent align="center">
          <Avatar
            src={`${s3StaticImagePath}/1756979958-kqlxrxH0E9.webp`}
            alt={app_title}
            sx={{ maxWidth: "200px", width: "100%", height: "unset", margin: "auto", marginBottom: "32px" }}
            variant="square"
          />
          <Typography variant="h6" mb={2}>
            {t("Unfortunatetly your application has been rejected.")}
          </Typography>
          <Typography variant="body1" mb={2}>
            {t("Do you want to apply for a Gold Loan?")}
          </Typography>
          <Grid container spacing={2} sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            marginTop: 2,
          }}>
            <Button variant="contained" color="primary" onClick={() => handleRejection()}>
              Yes
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>}
    </>
  );
}

export default MuthootFincorp;
